import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconPlusCircle } from "@/assets/icons/geist/IconPlusCircle";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { cn } from "@/components/podkit/lib/cn";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { Text } from "@/components/podkit/typography/Text";
import { Heading3, Heading4 } from "@/components/podkit/typography/Headings";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useDeleteDomainVerification,
    useListDomainVerifications,
    useVerifyDomain,
    type PlainDomainVerification,
} from "@/queries/organization-queries";
import { CustomSSOConfigCardV2 } from "@/routes/settings/login/SSOConfigCardV2";
import { SetupSSOModal, type SetupSSOModalStep } from "@/routes/settings/login/SetupSSOModal";
import { DomainVerificationV2 } from "@/routes/settings/login/DomainVerificationV2";
import { type FC, useCallback, useMemo, useState } from "react";
import { DomainVerificationState, type SSOConfiguration } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Tooltip } from "@/components/Tooltip";

type CustomSSOSectionProps = {
    organizationId: string;
    configs: SSOConfiguration[];
    deactivateable: boolean;
    editable: boolean;
    className?: string;
};

type ModalState = {
    step: SetupSSOModalStep;
    config?: SSOConfiguration;
};

type DomainDialogState = {
    domain: string;
    error?: string;
};

export const CustomSSOSection: FC<CustomSSOSectionProps> = ({
    organizationId,
    configs,
    deactivateable,
    editable,
    className,
}) => {
    const { toast } = useToast();
    const [modalState, setModalState] = useState<ModalState | undefined>(undefined);
    const [newDomainModalOpen, setNewDomainModalOpen] = useState(false);
    const [dnsDialogState, setDnsDialogState] = useState<DomainDialogState | undefined>(undefined);
    const [verifyingDomainId, setVerifyingDomainId] = useState<string | undefined>(undefined);
    const [domainToDelete, setDomainToDelete] = useState<PlainDomainVerification | undefined>(undefined);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

    const { data: domainVerifications = [] } = useListDomainVerifications();
    const deleteDomainVerification = useDeleteDomainVerification();

    const verifyDomain = useVerifyDomain();

    const domains = useMemo(() => {
        return domainVerifications
            .filter(
                (verification) =>
                    verification.state === DomainVerificationState.PENDING ||
                    verification.state === DomainVerificationState.VERIFIED,
            )
            .sort((a, b) => {
                if (a.state !== b.state) {
                    return a.state === DomainVerificationState.PENDING ? -1 : 1;
                }
                return a.domain.localeCompare(b.domain);
            });
    }, [domainVerifications]);

    const openSetupModal = useCallback((step: SetupSSOModalStep, config?: SSOConfiguration) => {
        setModalState({ step, config });
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalState(undefined);
    }, []);

    const handleCreateNewSSOConfig = useCallback(() => {
        openSetupModal("configure-sso-v2");
    }, [openSetupModal]);

    const handleEdit = useCallback(
        (config: SSOConfiguration) => {
            openSetupModal("configure-sso", config);
        },
        [openSetupModal],
    );

    const handleNewDomain = useCallback(() => {
        setNewDomainModalOpen(true);
    }, []);

    const handleCloseNewDomainModal = useCallback(() => {
        setNewDomainModalOpen(false);
    }, []);

    const handleShowDnsRecord = useCallback((domain: string) => {
        setDnsDialogState({ domain });
    }, []);

    const handleCloseDnsDialog = useCallback(() => {
        setDnsDialogState(undefined);
    }, []);

    const handleVerifyPendingDomain = useCallback(
        async (verification: PlainDomainVerification) => {
            setVerifyingDomainId(verification.id);
            try {
                const result = await verifyDomain.mutateAsync(verification);
                const title =
                    result?.state === DomainVerificationState.VERIFIED
                        ? `${verification.domain} is verified.`
                        : `${verification.domain} is not yet verified. Please try again later.`;

                toast({ title });
            } catch (error) {
                setDnsDialogState({
                    domain: verification.domain,
                    error:
                        formatError(error) || "TXT record not found. DNS changes can take up to 48 hours to propagate.",
                });
            } finally {
                setVerifyingDomainId(undefined);
            }
        },
        [toast, verifyDomain],
    );

    const handleRequestDeleteDomain = useCallback((verification: PlainDomainVerification) => {
        setDeleteError(undefined);
        setDomainToDelete(verification);
    }, []);

    const handleCloseDeleteDialog = useCallback(() => {
        setDeleteError(undefined);
        setDomainToDelete(undefined);
    }, []);

    const handleConfirmDeleteDomain = useCallback(async () => {
        if (!domainToDelete) {
            return;
        }

        try {
            await deleteDomainVerification.mutateAsync(domainToDelete.id);
            toast({
                title: `${domainToDelete.domain} has been deleted.`,
            });
            handleCloseDeleteDialog();
        } catch (error) {
            setDeleteError(formatError(error));
        }
    }, [deleteDomainVerification, domainToDelete, handleCloseDeleteDialog, toast]);

    return (
        <div className={cn("flex flex-col gap-4", className)}>
            <div className="flex flex-wrap items-center gap-2">
                <Heading3>Single Sign On</Heading3>
                <div className="ml-auto">
                    <Button
                        disabled={!editable}
                        variant="secondary"
                        size="sm"
                        LeadingIcon={IconPlusCircle}
                        onClick={handleCreateNewSSOConfig}
                        data-tracking-id="create-custom-sso"
                    >
                        New SSO
                    </Button>
                </div>
            </div>

            {configs.length === 0 ? (
                <Card className="flex w-full flex-col gap-2 bg-surface-glass p-4" variant="bordered">
                    <Text className="text-base text-content-primary">No custom SSO configurations yet.</Text>
                    <Text className="text-sm text-content-secondary">
                        Create an SSO configuration to restrict access based on email domain.
                    </Text>
                </Card>
            ) : (
                <div className="flex flex-col gap-3">
                    {configs.map((config) => (
                        <CustomSSOConfigCardV2
                            key={config.id}
                            config={config}
                            deactivateable={deactivateable}
                            editable={editable}
                            onEdit={handleEdit}
                            domainVerifications={domainVerifications}
                            onNewDomain={handleNewDomain}
                        />
                    ))}
                </div>
            )}

            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Heading4>Login Domains</Heading4>
                    <div className="ml-auto">
                        <Button
                            disabled={!editable}
                            variant="secondary"
                            size="sm"
                            LeadingIcon={IconPlusCircle}
                            onClick={handleNewDomain}
                            data-tracking-id="create-domain-verification"
                        >
                            New domain
                        </Button>
                    </div>
                </div>

                {domains.length === 0 ? (
                    <Card className="flex flex-col gap-2 bg-surface-glass p-4" variant="bordered">
                        <Text className="text-base text-content-primary">No email domains yet.</Text>
                        <Text className="text-sm text-content-secondary">
                            Add an email domain to control who can sign in with SSO.
                        </Text>
                    </Card>
                ) : (
                    <div className="flex flex-col gap-3">
                        {domains.map((verification) => (
                            <DomainCard
                                key={verification.id}
                                verification={verification}
                                editable={editable}
                                onShowDnsRecord={handleShowDnsRecord}
                                onVerify={handleVerifyPendingDomain}
                                verifyingDomainId={verifyingDomainId}
                                onDelete={handleRequestDeleteDomain}
                            />
                        ))}
                    </div>
                )}
            </div>

            {modalState && (
                <SetupSSOModal
                    organizationId={organizationId}
                    config={modalState.config}
                    step={modalState.step}
                    onClose={handleCloseModal}
                />
            )}

            {newDomainModalOpen && (
                <Dialog open onOpenChange={(open) => !open && handleCloseNewDomainModal()}>
                    <DialogContent
                        className="flex max-w-[600px] flex-col p-6"
                        data-track-location={TrackLocations.SetupSSOModal}
                    >
                        <DomainVerificationV2 organizationId={organizationId} onCancel={handleCloseNewDomainModal} />
                    </DialogContent>
                </Dialog>
            )}

            {dnsDialogState && (
                <Dialog open onOpenChange={(open) => !open && handleCloseDnsDialog()}>
                    <DialogContent
                        className="flex max-w-[600px] flex-col gap-4 p-6"
                        data-track-location={TrackLocations.SetupSSOModal}
                    >
                        {dnsDialogState.error && (
                            <Banner
                                variant="danger"
                                text={
                                    <div className="flex flex-col text-sm">
                                        <span>{dnsDialogState.error}</span>
                                        <span className="text-content-secondary">
                                            DNS changes can take up to 48 hours to propagate.
                                        </span>
                                    </div>
                                }
                            />
                        )}
                        <DomainVerificationV2
                            organizationId={organizationId}
                            domain={dnsDialogState.domain}
                            onCancel={handleCloseDnsDialog}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {domainToDelete && (
                <Dialog open onOpenChange={(open) => !open && handleCloseDeleteDialog()}>
                    <DialogContent
                        className="flex max-w-[480px] flex-col gap-4 p-6"
                        data-track-location={TrackLocations.SetupSSOModal}
                    >
                        <DialogHeader>
                            <DialogTitle>Delete {domainToDelete.domain}?</DialogTitle>
                            <DialogDescription>
                                This will remove the domain from your SSO configuration. This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        {deleteError && <Banner variant="danger" text={deleteError} />}
                        <DialogFooter className="gap-2">
                            <DialogClose asChild>
                                <Button
                                    variant="outline"
                                    onClick={handleCloseDeleteDialog}
                                    data-tracking-id="cancel-delete-domain"
                                >
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDeleteDomain}
                                loading={deleteDomainVerification.isPending}
                                data-tracking-id="confirm-delete-domain"
                            >
                                Delete
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

type DomainCardProps = {
    verification: PlainDomainVerification;
    editable: boolean;
    onShowDnsRecord: (domain: string) => void;
    onVerify: (verification: PlainDomainVerification) => void;
    verifyingDomainId?: string;
    onDelete: (verification: PlainDomainVerification) => void;
};

const DomainCard: FC<DomainCardProps> = ({ verification, editable, onShowDnsRecord, verifyingDomainId, onDelete }) => {
    const isPending = verification.state === DomainVerificationState.PENDING;
    const isVerifying = verifyingDomainId === verification.id;

    const domainInfo = (
        <div className="flex items-start gap-3">
            {isPending ? (
                <IconWarningCircle className="mt-1 text-content-warning" size="sm" />
            ) : (
                <IconCheckCircle className="mt-1 text-content-positive" size="sm" />
            )}
            <div className="flex flex-col gap-1">
                <Text className="text-base font-medium text-content-primary">{verification.domain}</Text>
                {isPending ? (
                    <div className="flex flex-wrap items-center gap-2 text-sm text-content-secondary">
                        <Button
                            variant="text"
                            size="xs"
                            className="p-0 text-content-link hover:underline"
                            onClick={() => onShowDnsRecord(verification.domain)}
                            data-testid={`verification-required-${verification.domain}`}
                            data-tracking-id="verification-required-link"
                            aria-label={`View DNS instructions for ${verification.domain}`}
                            disabled={!editable}
                        >
                            Verify ownership
                        </Button>
                    </div>
                ) : (
                    <Text className="text-sm text-content-positive">Verified</Text>
                )}
            </div>
        </div>
    );

    return (
        <Card
            className="flex flex-col gap-4 bg-surface-glass p-4 md:flex-row md:items-center md:justify-between"
            data-testid={`domain-card-${verification.domain}`}
        >
            {isPending ? (
                <Tooltip content="Domain not verified. Add a TXT record to your DNS settings to verify ownership.">
                    {domainInfo}
                </Tooltip>
            ) : (
                domainInfo
            )}

            <div className="flex items-center gap-2 self-stretch md:self-center">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            LeadingIcon={IconEllipsisHorizontal}
                            aria-label={`Domain actions for ${verification.domain}`}
                            disabled={!editable}
                            data-tracking-id="domain-actions-trigger"
                        />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {isPending && (
                            <DropdownMenuItem
                                onClick={() => onShowDnsRecord(verification.domain)}
                                disabled={!editable || isVerifying}
                                data-tracking-id="verify-domain-card"
                            >
                                Verify
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={() => onDelete(verification)}
                            data-tracking-id="delete-domain-card"
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </Card>
    );
};
